import { computed, effect, inject, Injectable, Signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs';
import { CartItem, selectCart, selectIsCartOwner, selectIsNewOrder, selectItemsGroupedByMember } from './cart.reducer';
import { AuthService, selectPersistedUserId } from '@us/web/auth/data-access';

interface CartState {
    currentTabIndex: 0 | 1;
}

@UntilDestroy()
@Injectable()
export class CartPageStore extends ComponentStore<CartState> implements OnStoreInit {
    private readonly store = inject(Store);
    private readonly authService = inject(AuthService);

    private readonly cart = this.store.selectSignal(selectCart);
    private readonly userId = this.store.selectSignal(selectPersistedUserId);
    readonly isCartOwner = this.store.selectSignal(selectIsCartOwner(this.userId()));

    private readonly _itemsGroupedByMember: Signal<
        {
            is_owner: boolean;
            items: CartItem[];
            id: string;
            name: string;
        }[]
    > = this.store.selectSignal(selectItemsGroupedByMember);

    readonly itemsGroupedByMember = computed(() => {
        const items = this._itemsGroupedByMember();
        const userId = this.userId();

        const currentUserItems = items.find(item => item.id === userId);
        if (currentUserItems) {
            items.splice(items.indexOf(currentUserItems), 1);
            items.unshift(currentUserItems);
        }
        return items;
    });

    readonly currentTabIndex$ = this.select(state => state.currentTabIndex);

    constructor() {
        super(<CartState>{
            currentTabIndex: 0
        });
    }

    readonly setCurrentTabIndex = this.updater(
        (state, currentTabIndex: number): CartState => ({
            ...state,
            currentTabIndex: currentTabIndex as 0 | 1
        })
    );

    ngrxOnStoreInit(): void {
        this.store
            .select(selectIsNewOrder)
            .pipe(
                untilDestroyed(this),
                filter(isNewOrder => isNewOrder),
                tap(() => {
                    this.setCurrentTabIndex(0);
                })
            )
            .subscribe();
    }
}
